import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { videos } from "@/mocks/videos";
import { categories, categoryName } from "@/mocks/categories";

interface FeedProps {
  search: string;
  activeCat: string;
  onSelectCat: (id: string) => void;
}

export default function Feed({ search, activeCat, onSelectCat }: FeedProps) {
  const [liked, setLiked] = useState<string[]>([]);

  const filtered = useMemo(() => {
    const q = search.trim();
    const base = videos.filter((v) => {
      if (!q) return true;
      const hay = `${v.title}${v.author}${v.tags.join("")}${categoryName(v.categoryId)}`;
      return hay.includes(q);
    });
    if (q) return base;
    if (activeCat === "all") return base;
    return base.filter((v) => v.categoryId === activeCat);
  }, [search, activeCat]);

  const toggleLike = (id: string) => {
    setLiked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const tabs = [{ id: "all", name: "全部", count: videos.length }, ...categories];

  return (
    <section id="stream" className="scroll-mt-24 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <SectionHeading
          eyebrow="Fresh Melons"
          title="瓜料速递，热乎着来"
          desc="最新整理的热门视频与图文，支持按分类与关键词筛选，实时看到当下最火的内容。"
        />

        <div className="mb-7 flex flex-wrap items-center gap-2">
          {tabs.map((t) => {
            const searching = search.trim().length > 0;
            const isActive = !searching && activeCat === t.id;
            const activeCls = isActive
              ? "bg-primary-500 text-background-50"
              : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200";
            const countCls = isActive ? "text-background-100" : "text-secondary-600";
            return (
              <button
                key={t.id}
                onClick={() => {
                  if (searching) return;
                  onSelectCat(t.id);
                }}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${activeCls}`}
              >
                {t.name}
                <span className={`text-[11px] ${countCls}`}>{t.count}</span>
              </button>
            );
          })}
          {search.trim() && (
            <span className="flex items-center gap-2 rounded-full border border-accent-300 bg-accent-50 px-3 py-2 text-sm font-medium text-accent-800">
              <i className="ri-search-line"></i>
              关键词“{search.trim()}”共 {filtered.length} 条
            </span>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-secondary-300 bg-background-100/60 py-20 text-center">
            <i className="ri-inbox-archive-line text-4xl text-secondary-500"></i>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-800">暂时没有找到相关瓜料</p>
            <p className="mt-1 text-sm text-foreground-600">换个关键词，或清空筛选后再试试</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((v) => {
              const isLiked = liked.includes(v.id);
              return (
                <article key={v.id} className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/80 bg-background-100/50 transition-all hover:-translate-y-0.5 hover:border-primary-200">
                  <div className="relative block overflow-hidden">
                    <img
                      src={v.imageUrl}
                      alt={`${v.title} - 吃瓜视频${categoryName(v.categoryId)}视频`}
                      className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/40 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div>
                    {v.badge && (
                      <span className="absolute left-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 text-[11px] font-black text-background-50">
                        {v.badge}
                      </span>
                    )}
                    <span className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-primary-950/70 px-2.5 py-1 text-[11px] font-semibold text-background-50 backdrop-blur">
                      <i className="ri-play-mini-fill text-sm text-accent-400"></i>
                      {v.duration}
                    </span>
                    <span className="absolute right-3 top-3 whitespace-nowrap rounded-full bg-background-50/90 px-2 py-1 text-[11px] font-semibold text-primary-700">
                      {categoryName(v.categoryId)}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <h3 className="font-heading text-[15px] font-bold leading-snug text-foreground-950">
                      {v.title}
                    </h3>
                    <p className="mt-2 hidden text-sm leading-6 text-foreground-600 sm:block">{v.summary}</p>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {v.tags.map((tag) => (
                        <span key={tag} className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <div className="mt-auto flex items-center justify-between gap-3 pt-4">
                      <span className="min-w-0 flex items-center gap-1.5 text-xs text-foreground-600">
                        <span className="truncate font-medium text-foreground-800">{v.author}</span>
                        <span className="text-foreground-400">·</span>
                        <span className="whitespace-nowrap">{v.time}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1 text-[11px] text-foreground-400">
                        <i className="ri-eye-line"></i> {v.views}
                      </span>
                      <button
                        onClick={() => toggleLike(v.id)}
                        aria-label={isLiked ? "取消点赞" : "点赞"}
                        className={`flex h-8 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold transition-colors ${
                          isLiked ? "bg-accent-100 text-accent-700" : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                        }`}
                      >
                        <i className={`text-sm ${isLiked ? "ri-heart-fill" : "ri-heart-line"}`}></i>
                        {isLiked ? v.comments + 1 : v.comments}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}